import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useRef, useState } from "react";
import {
  CampaignWritingMode,
  TemplateError,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Map } from "schemas/functions";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { DAYS_TO_DELAY_FOLLOWUP } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";
import { PersonalizeToneOption } from "features/Influencer/QuickSendIndividualDrawer/Dialogs/PersonalizeDialog/schema";
import { fetcherAuth } from "utils/api";
import { useRefreshChatCredits } from "utils/useRefreshChatCredits";

import StepNav from "../../StepNav";
import builderStyles from "../../styles";
import Templates from "../Templates";
import AiBrief from "./AiBrief";
import Generating, { GenBrand } from "./Generating";
import ModeChoice from "./ModeChoice";
import Review from "./Review";
import { PersonalizationBrief } from "./constants";
import styles from "./styles";

interface Props {
  handleUpdateCampaign: (
    body: Map,
    skipMoveNext?: boolean,
  ) => Promise<boolean | void>;
  moveBack?: () => void;
  submitLoading?: boolean;
  errorMessage?: string;
  templateError?: TemplateError | null;
  onClearTemplateError?: () => void;
}

type Phase = "mode" | "generating" | "review";

const POLL_INTERVAL_MS = 5000;
const LARGE_CAMPAIGN_THRESHOLD = 10;
const CREDIT_CONFIRM_THRESHOLD = 100;

export default function WriteEmails({
  handleUpdateCampaign,
  moveBack,
  submitLoading = false,
}: Props) {
  const { getToken } = useAuth();
  const { currentOrg, emailSettings } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { campaign } = useContext(NewCampaignContext);
  const {
    templates: allTemplates,
    outreachTemplates,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);
  const { isFreePlan, subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const refreshCredits = useRefreshChatCredits();

  const locked = isFreePlan;
  const [mode, setMode] = useState<CampaignWritingMode>(
    locked
      ? CampaignWritingMode.template
      : campaign?.writingMode ?? CampaignWritingMode.ai,
  );
  const [phase, setPhase] = useState<Phase>("mode");
  const [resolvingResume, setResolvingResume] = useState(
    !locked && campaign?.writingMode === CampaignWritingMode.ai,
  );
  const [brief, setBrief] = useState<PersonalizationBrief>({
    startingTemplateId: campaign?.personalizationBrief?.startingTemplateId,
    emphasis: campaign?.personalizationBrief?.emphasis ?? [],
    tone: campaign?.personalizationBrief?.tone as
      | PersonalizeToneOption
      | null
      | undefined,
    notes: campaign?.personalizationBrief?.notes ?? "",
  });
  const [followups, setFollowups] = useState<Template[]>([]);
  const [generating, setGenerating] = useState(false);
  const [draftsDoneCount, setDraftsDoneCount] = useState(0);
  const [largeGenWarnOpen, setLargeGenWarnOpen] = useState(false);
  const [summary, setSummary] = useState<{
    total: number;
    done: number;
    brands: GenBrand[];
  }>({ total: 0, done: 0, brands: [] });

  const brandCount = campaign?.campaignBrandCount ?? 0;
  const creditBalance = subscription?.remainingChatMessages ?? 0;
  const creditsNeeded = Math.max(0, brandCount - draftsDoneCount);
  const enoughCredits = creditBalance >= creditsNeeded;
  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const openUpgrade = () =>
    setUpgradeDialogSource(
      UpgradeDialogSource.hits_personalized_ai_messages_limit,
    );

  useEffect(() => {
    if (outreachTemplates?.length === 0) fetchOutreachTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const followupsHydratedRef = useRef(false);
  useEffect(() => {
    if (followupsHydratedRef.current || !allTemplates?.length) return;
    const settingTemplates =
      campaign?.emailSetting?.emailSettingTemplates ?? [];
    if (settingTemplates.length <= 1) return;
    followupsHydratedRef.current = true;
    setFollowups(
      settingTemplates
        .slice(1)
        .map((st) => {
          const template = allTemplates.find((x) => x.id === st.templateId);
          return template
            ? { ...template, delayDays: st.delayDays ?? DAYS_TO_DELAY_FOLLOWUP }
            : null;
        })
        .filter(Boolean) as Template[],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length]);

  const startingTemplateInitRef = useRef(false);
  useEffect(() => {
    if (startingTemplateInitRef.current || !allTemplates?.length) return;
    startingTemplateInitRef.current = true;
    if (brief.startingTemplateId) return;
    const presetTemplates = campaign?.emailSetting?.emailSettingTemplates
      ?.length
      ? campaign.emailSetting.emailSettingTemplates
      : emailSettings?.emailSettingTemplates ?? [];
    const presetId = presetTemplates[0]?.templateId;
    const defaultId =
      presetId && allTemplates.some((t) => t.id === presetId)
        ? presetId
        : filteredOutreachTemplates?.[0]?.id ?? null;
    if (defaultId)
      setBrief((prev) => ({ ...prev, startingTemplateId: defaultId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length]);

  const fetchStatus = async () => {
    if (!currentOrg || !campaign?.id) return null;
    return fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/campaigns/${campaign.id}/outreach-drafts/status`,
      "GET",
    );
  };

  const continueToLaunch = () =>
    handleUpdateCampaign({ writingMode: CampaignWritingMode.ai }, false);

  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !currentOrg?.id || !campaign?.id) return;
    if (campaign.writingMode !== CampaignWritingMode.ai) return;
    resumedRef.current = true;
    fetchStatus()
      .then((status) => {
        if (!status || status.total === 0) return;
        const selectedCount = campaign.campaignBrandCount ?? 0;
        setDraftsDoneCount(
          Math.max(0, selectedCount - (status.needingGeneration ?? 0)),
        );
        // Selection changed since these drafts — let the user regenerate, not resume.
        if (selectedCount !== status.total) return;
        setSummary({
          total: status.total,
          done: status.done,
          brands: status.brands ?? [],
        });
        if (status.pending > 0 || status.generating > 0) {
          setPhase("generating");
        } else {
          setPhase("review");
        }
      })
      .catch(() => undefined)
      .finally(() => setResolvingResume(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, campaign?.id]);

  const pollRef = useRef<ReturnType<typeof setInterval>>();
  useEffect(() => {
    if (phase !== "generating") return undefined;
    const tick = async () => {
      try {
        const status = await fetchStatus();
        if (!status) return;
        setSummary({
          total: status.total,
          done: status.done,
          brands: status.brands ?? [],
        });
        const settled = status.pending === 0 && status.generating === 0;
        if (settled && status.total > 0) {
          clearInterval(pollRef.current);
          refreshCredits();
          setPhase("review");
        }
      } catch (error) {
        setErrorAlert(error);
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Templates' handleUpdateCampaign prop is stricter (Promise<boolean>); coerce.
  const handleUpdateForTemplates = async (
    body: Map,
    skipMoveNext?: boolean,
  ): Promise<boolean> =>
    Boolean(await handleUpdateCampaign(body, skipMoveNext));

  const handleModeChange = (next: CampaignWritingMode) => {
    setMode(next);
    if (next !== campaign?.writingMode) {
      handleUpdateCampaign({ writingMode: next }, true);
    }
  };

  const handleGenerate = async () => {
    if (!currentOrg || !campaign?.id) return;
    setGenerating(true);
    try {
      await handleUpdateCampaign(
        {
          writingMode: CampaignWritingMode.ai,
          personalizationBrief: brief,
          templateIds: brief.startingTemplateId
            ? [brief.startingTemplateId, ...followups.map((f) => f.id)]
            : [],
          templateDelayDays: brief.startingTemplateId
            ? [
                null,
                ...followups.map((f) => f.delayDays ?? DAYS_TO_DELAY_FOLLOWUP),
              ]
            : [],
        },
        true,
      );
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaign.id}/outreach-drafts/generate`,
        "POST",
        {},
        {},
      );
      setPhase("generating");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (brandCount > LARGE_CAMPAIGN_THRESHOLD) {
      setLargeGenWarnOpen(true);
      return;
    }
    handleGenerate();
  };

  if (resolvingResume) {
    return (
      <Box sx={styles.resumeLoader}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (phase === "generating") {
    return (
      <Generating
        total={summary.total || brandCount}
        done={summary.done}
        brands={summary.brands}
      />
    );
  }

  if (phase === "review") {
    return (
      <Review
        campaignId={campaign?.id as number}
        brandCount={brandCount}
        onBack={() => setPhase("mode")}
        onContinue={continueToLaunch}
        onRetryFailed={handleGenerate}
      />
    );
  }

  return (
    <>
      <Box sx={builderStyles.formColumn}>
        <Box sx={styles.creditChipRow}>
          <Box sx={styles.creditChip}>
            <Box component="i" className="fa-solid fa-coins" />
            {creditBalance.toLocaleString()} AI credits left
          </Box>
        </Box>

        <Box sx={styles.callout}>
          <Typography sx={styles.calloutTitle}>
            <Box component="i" className="fa-solid fa-envelope-open-text" />
            Craft your outreach
          </Typography>
          <Typography sx={styles.calloutBody}>
            Choose how you want to write this campaign&apos;s pitches. Bento can
            draft a unique, personalized email for every brand — or you can send
            one template to everyone.
          </Typography>
        </Box>

        <ModeChoice
          mode={mode}
          onChange={handleModeChange}
          brandCount={brandCount}
          locked={locked}
          onUpgrade={openUpgrade}
        />

        {mode === CampaignWritingMode.ai && !locked && (
          <>
            {!enoughCredits && (
              <Box sx={styles.creditBanner}>
                <Box sx={styles.bannerIcon("warn")}>
                  <Box component="i" className="fa-solid fa-coins" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={styles.bannerTitle}>
                    Not enough credits to personalize all {brandCount} brands
                  </Typography>
                  <Typography sx={styles.bannerBody}>
                    This needs <strong>{creditsNeeded} credits</strong> (1 per
                    pitch) but you have <strong>{creditBalance}</strong> left.
                    Buy more credits, or use one template for everyone instead —
                    that&apos;s free.
                  </Typography>
                </Box>
                <StyledLargeButton color="primary" onClick={openUpgrade}>
                  Buy credits
                </StyledLargeButton>
              </Box>
            )}

            <AiBrief
              brief={brief}
              onChange={setBrief}
              outreachTemplates={filteredOutreachTemplates ?? []}
              brandCount={brandCount}
              creditsNeeded={creditsNeeded}
              alreadyGenerated={draftsDoneCount}
              highUsage={creditsNeeded > CREDIT_CONFIRM_THRESHOLD}
              followups={followups}
              onFollowupsChange={setFollowups}
            />
            <StepNav
              onBack={() => moveBack?.()}
              onNext={enoughCredits ? handleGenerateClick : openUpgrade}
              nextLabel={
                enoughCredits
                  ? `Generate ${creditsNeeded} personalized drafts · ${creditsNeeded} credits`
                  : "Buy more credits"
              }
              nextIcon={
                enoughCredits ? "fa-solid fa-sparkles" : "fa-solid fa-coins"
              }
              nextDisabled={
                enoughCredits && (!brief.startingTemplateId || brandCount === 0)
              }
              nextLoading={generating || submitLoading}
            />
          </>
        )}
      </Box>

      {mode === CampaignWritingMode.template && (
        <Templates
          handleUpdateCampaign={handleUpdateForTemplates}
          moveBack={moveBack}
          submitLoading={submitLoading}
        />
      )}

      <Dialog
        open={largeGenWarnOpen}
        onClose={() => setLargeGenWarnOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Generating {brandCount} pitches can take a few minutes
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Bento writes a unique email for each brand, so larger campaigns take
            a while. You can leave this page — we&apos;ll keep generating, and
            you can come back to review.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLargeGenWarnOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setLargeGenWarnOpen(false);
              handleGenerate();
            }}
            sx={{ textTransform: "none" }}
          >
            Generate {creditsNeeded} drafts
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
